import { client, databases, databaseId, appwriteConfig } from '../config/appwrite';
import { Article, ArticleStatus, User, UserRole } from '../types';
import { ID, Query } from 'appwrite';
import { formatTags, parseTags } from '../utils/tagUtils';
import { getScheduledAtTime, isWithinScheduleTolerance } from '../utils/dateTimeUtils';
import { StorageService } from './storageService';
import { AuthService } from './authService';
import { resolveArticleMetadata } from '../utils/articleMetadata';

export class ArticleService {
  static subscribeToUpdates(user: User, onUpdate: (article: Article) => void): () => void {
    return client.subscribe(`databases.${databaseId}.collections.${appwriteConfig.collections.articles}.documents`, response => {
      const doc = response.payload as any;
      if (!doc.$id || !doc.authorId || !response.events.some(event => event.endsWith('.update'))) return;
      if (doc.authorId === user.id || this.hasRole(user, ['administrator', 'redakteur'])) {
        onUpdate(this.mapDocumentToArticle(doc));
      }
    });
  }
  // Artikel erstellen
  static async createArticle(articleData: Partial<Article>, user: User): Promise<Article> {
    try {
      if (articleData.publicationState === 'queued' && (articleData.status !== 'ready'
          || !/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,35}$/.test(articleData.publicationRequestId || '') || !articleData.publicationPayload)) {
        throw new Error('Nur ein vollständig gespeicherter, eingereichter Artikel kann zur Veröffentlichung beauftragt werden.');
      }
      const article = await databases.createDocument(
        databaseId,
        appwriteConfig.collections.articles,
        ID.unique(),
        {
          authorId: articleData.authorId || user.id,
          platformId: articleData.platformId,
          status: articleData.status || 'draft',
          type: articleData.type || 'text', // Standard ist 'text', für Audio-Artikel 'audio'
          imageType: articleData.imageType || 'redaktion',
          title: articleData.title || '',
          content: articleData.content || '',
          originalContent: articleData.originalContent || articleData.content || '',
          categoryId: articleData.categoryId || '',
          dossierId: articleData.dossierId || '',
          topic: articleData.topic || '',
          subtitle: articleData.subtitle || '',
          teaser: articleData.teaser || '',
          ...resolveArticleMetadata(articleData),
          tags: formatTags(articleData.tags),
          imagePrompt: articleData.imagePrompt || '',
          imageSource: articleData.imageSource || '',
          imageSourceUrl: articleData.imageSourceUrl || '',
          imageStorageUrl: articleData.imageStorageUrl || '',
          imageCaption: articleData.imageCaption || '',
          imageCaptionFromPrompt: articleData.imageCaptionFromPrompt || '',
          lektoratIntensity: articleData.lektoratIntensity || '',
          lektoratContent: articleData.lektoratContent || '',
          originalArticleUrl: articleData.originalArticleUrl || '', // Audio-Artikel Feld
          originalArticleTitle: articleData.originalArticleTitle || '', // Audio-Artikel Feld
          audioStorageUrl: articleData.audioStorageUrl || '', // Audio-Artikel Feld
          sources: articleData.sources ? articleData.sources.map(s => JSON.stringify(s)) : [], // Sources als String-Array
          revisions: '', // revisions wird als String gespeichert
          scheduledAt: articleData.scheduledAt || null,
          publishedAt: null,
          ...(articleData.publicationState ? {
            publicationState: articleData.publicationState,
            publicationRequestId: articleData.publicationRequestId,
            publicationPayload: articleData.publicationPayload,
            publicationError: articleData.publicationError || ''
          } : {})
          // createdAt und updatedAt werden von Appwrite automatisch gesetzt
        }
      );

      return this.mapDocumentToArticle(article);
    } catch (error) {
      // Verbesserte Fehlermeldung für Zeichenlimit-Fehler
      if (error instanceof Error && error.message.includes('20000')) {
        throw new Error('Artikel konnte nicht erstellt werden: Der Artikeltext ist zu lang. Bitte kürze ihn oder kontaktiere die Administration.');
      }
      throw new Error(`Artikel konnte nicht erstellt werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  }

  // Artikel abrufen (mit Berechtigungsprüfung)
  static async getArticle(articleId: string, user: User): Promise<Article> {
    try {
      const article = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.articles,
        articleId
      );

      const mappedArticle = this.mapDocumentToArticle(article);

      // Berechtigung prüfen
      if (!this.canAccessArticle(mappedArticle, user)) {
        throw new Error('Keine Berechtigung für diesen Artikel');
      }

      return mappedArticle;
    } catch (error) {
      throw new Error(`Artikel konnte nicht abgerufen werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  }

  // Artikel abrufen (ohne Berechtigungsprüfung - für Edit-Seite)
  static async getArticleById(articleId: string): Promise<Article> {
    try {
      const article = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.articles,
        articleId
      );

      return this.mapDocumentToArticle(article);
    } catch (error) {
      throw new Error(`Artikel konnte nicht abgerufen werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  }

  // Artikel aktualisieren
  static async updateArticle(articleId: string, updates: Partial<Article>, user: User): Promise<Article> {
    try {
      const currentArticle = await this.getArticle(articleId, user);

      if (currentArticle.publicationRequestId && ['queued', 'sending'].includes(currentArticle.publicationState || '')) {
        throw new Error('Der Artikel wird im Hintergrund verarbeitet. Änderungen sind nach Abschluss der Übergabe wieder möglich.');
      }

      if (updates.authorId !== undefined && updates.authorId !== currentArticle.authorId) {
        if (!this.hasRole(user, ['administrator', 'redakteur'])) {
          throw new Error('Nur Administratoren und Redakteure dürfen den Autor wechseln.');
        }
        if (currentArticle.status !== 'draft') {
          throw new Error('Der Autor kann nur bei einem Entwurf gewechselt werden.');
        }
        if (!updates.authorId.trim()) {
          throw new Error('Bitte wähle einen Autor aus.');
        }
        await AuthService.getUserById(updates.authorId);
      }

      // Status-Übergang prüfen
      if (updates.status && !this.isValidStatusTransition(currentArticle.status, updates.status, user)) {
        throw new Error('Ungültiger Status-Übergang');
      }

      // Veröffentlichungstermin-Berechtigung:
      // - Entwurf: Autor oder Redaktion/Admin
      // - Bereits eingereicht/weiterer Verlauf: nur Redaktion/Admin
      if (updates.scheduledAt !== undefined && updates.scheduledAt !== currentArticle.scheduledAt) {
        const isEditorOrAdmin = this.hasRole(user, ['administrator', 'redakteur']);
        const isAuthor = currentArticle.authorId === user.id;
        const canEditDraftSchedule = currentArticle.status === 'draft' && (isAuthor || isEditorOrAdmin);
        const canEditSubmittedSchedule = currentArticle.status !== 'draft' && isEditorOrAdmin;

        if (!canEditDraftSchedule && !canEditSubmittedSchedule) {
          throw new Error('Keine Berechtigung zum Ändern des Veröffentlichungstermins');
        }
      }

      // Revision erstellen, wenn sich der Inhalt geändert hat (nur für bestimmte Status)
      if (updates.content && updates.content !== currentArticle.content && currentArticle.status !== 'draft') {
        try {
          await this.createRevision(currentArticle, user.id);
        } catch (revisionError) {
          // Revision-Fehler nicht blockieren, nur loggen
          console.warn('Revision konnte nicht erstellt werden:', revisionError);
        }
      }

      // Sources in String-Array konvertieren, falls vorhanden
      const updatesForDb: any = { ...updates };
      if (updates.sources !== undefined) {
        updatesForDb.sources = updates.sources.map(s => JSON.stringify(s));
      }

      const updatedArticle = await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.articles,
        articleId,
        updatesForDb
      );

      return this.mapDocumentToArticle(updatedArticle);
    } catch (error) {
      // Verbesserte Fehlermeldung für Zeichenlimit-Fehler
      if (error instanceof Error && error.message.includes('20000')) {
        throw new Error('Artikel konnte nicht aktualisiert werden: Der Artikeltext ist zu lang. Bitte kürze ihn oder kontaktiere die Administration.');
      }
      throw new Error(`Artikel konnte nicht aktualisiert werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  }

  // Artikel löschen
  static async deleteArticle(articleId: string, user: User): Promise<void> {
    try {
      const article = await this.getArticle(articleId, user);

      // Nur Autor kann eigene Artikel löschen (im draft-Status)
      if (article.status !== 'draft' || article.authorId !== user.id) {
        throw new Error('Artikel kann nicht gelöscht werden');
      }

      await databases.deleteDocument(
        databaseId,
        appwriteConfig.collections.articles,
        articleId
      );
    } catch (error) {
      throw new Error('Artikel konnte nicht gelöscht werden');
    }
  }

  /**
   * Artikel löschen (Admin/Redakteur-Funktion)
   * Admins und Redakteure können alle Artikel löschen, unabhängig vom Status
   */
  static async deleteArticleAsAdmin(articleId: string, adminUser: User): Promise<void> {
    try {
      // Prüfe Admin/Redakteur-Berechtigung
      const isAdmin = adminUser.roles?.includes('administrator');
      const isRedakteur = adminUser.roles?.includes('redakteur');
      
      if (!isAdmin && !isRedakteur) {
        throw new Error('Keine Berechtigung zum Löschen von Artikeln');
      }

      // Artikel existiert prüfen (ohne Berechtigungsprüfung)
      const article = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.articles,
        articleId
      );

      if (!article) {
        throw new Error('Artikel nicht gefunden');
      }

      // Artikel aus Datenbank löschen
      await databases.deleteDocument(
        databaseId,
        appwriteConfig.collections.articles,
        articleId
      );
    } catch (error) {
      console.error('Fehler beim Löschen des Artikels:', error);
      throw new Error('Artikel konnte nicht gelöscht werden');
    }
  }

  // Artikel eines Benutzers abrufen
  static async getUserArticles(userId: string, currentUser: User, limit: number = 100): Promise<Article[]> {
    try {
      let query = Query.equal('authorId', userId);

      // Wenn Benutzer nicht Administrator/Redakteur ist, nur eigene Artikel
      if (!this.hasRole(currentUser, ['administrator', 'redakteur'])) {
        query = Query.equal('authorId', currentUser.id);
      }

      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.articles,
        [query, Query.orderDesc('$createdAt'), Query.limit(limit)]
      );

      return response.documents.map((doc: any) => this.mapDocumentToArticle(doc));
    } catch (error) {
      throw new Error('Artikel konnten nicht abgerufen werden');
    }
  }

  // Artikel eines Autors für Terminprüfung abrufen (nur letzte N)
  private static async getAuthorArticlesForScheduleCheck(authorId: string, limit: number = 5): Promise<Article[]> {
    try {
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.articles,
        [Query.equal('authorId', authorId), Query.orderDesc('$createdAt'), Query.limit(limit)]
      );

      return response.documents.map((doc: any) => this.mapDocumentToArticle(doc));
    } catch (error) {
      throw new Error('Artikel konnten nicht für die Terminprüfung geladen werden');
    }
  }

  // Prüfen ob ein Veröffentlichungstermin bereits belegt ist
  static async hasScheduleConflict(
    authorId: string,
    platformId: string,
    scheduledAt: Date,
    currentUser: User
  ): Promise<boolean> {
    try {
      const allArticles = await this.getAuthorArticlesForScheduleCheck(authorId, 5);
      
      return allArticles.some(article => {
        if (article.status === 'draft') return false;
        if (article.platformId !== platformId || !article.scheduledAt) return false;
        const scheduledTime = getScheduledAtTime(article.scheduledAt);
        return scheduledTime !== null && scheduledTime === scheduledAt.getTime();
      });
    } catch (error) {
      console.error('Fehler beim Prüfen des Schedule-Konflikts:', error);
      return false; // Bei Fehler kein Konflikt annehmen
    }
  }

  // Finde den Artikel, der mit einem Termin kollidiert (außer einem bestimmten Artikel)
  static async findConflictingArticle(
    authorId: string,
    platformId: string,
    scheduledAt: Date,
    currentUser: User,
    excludeArticleId?: string
  ): Promise<Article | null> {
    try {
      const allArticles = await this.getAuthorArticlesForScheduleCheck(authorId, 5);
      
      const conflicting = allArticles.find(article => 
        article.status !== 'draft' &&
        article.id !== excludeArticleId && // Nicht der ausgeschlossene Artikel
        article.platformId === platformId &&
        article.scheduledAt &&
        isWithinScheduleTolerance(article.scheduledAt, scheduledAt)
      );
      
      return conflicting || null;
    } catch (error) {
      console.error('Fehler beim Suchen des konfliktierenden Artikels:', error);
      return null;
    }
  }

  // Alle Artikel abrufen (für Redakteure/Administratoren)
  static async getAllArticles(user: User, limit: number = 100): Promise<Article[]> {
    try {
      if (!this.hasRole(user, ['administrator', 'redakteur'])) {
        throw new Error('Keine Berechtigung für alle Artikel');
      }

      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.collections.articles,
        [Query.orderDesc('$createdAt'), Query.limit(limit)]
      );

      return response.documents.map((doc: any) => this.mapDocumentToArticle(doc));
    } catch (error) {
      throw new Error('Artikel konnten nicht abgerufen werden');
    }
  }

  // Revision erstellen
  static async createRevision(article: Article, userId: string): Promise<void> {
    try {
      const revision = {
        id: ID.unique(),
        articleId: article.id,
        content: article.content,
        title: article.title,
        subtitle: article.subtitle,
        topic: article.topic,
        teaser: article.teaser,
        categoryId: article.categoryId,
        dossierId: article.dossierId,
        createdBy: userId,
        createdAt: new Date(),
      };

      const updatedRevisions = [...article.revisions, revision];

      await databases.updateDocument(
        databaseId,
        appwriteConfig.collections.articles,
        article.id,
        {
          revisions: JSON.stringify(updatedRevisions),
        }
      );
    } catch (error) {
      throw new Error('Revision konnte nicht erstellt werden');
    }
  }

  // Status-Übergang validieren
  private static isValidStatusTransition(currentStatus: ArticleStatus, newStatus: ArticleStatus, user: User): boolean {
    const allowedTransitions: Record<ArticleStatus, ArticleStatus[]> = {
      draft: ['lektoriert', 'ready', 'rework_requested'],
      lektoriert: ['metadata_ok', 'rework_requested'],
      metadata_ok: ['image_ok', 'rework_requested'],
      image_ok: ['ready', 'rework_requested'],
      ready: ['scheduled', 'rework_requested'],
      scheduled: ['publishing'],
      publishing: ['published', 'failed'],
      published: [],
      failed: ['ready'],
      rework_requested: ['draft', 'lektoriert'],
    };

    const allowed = allowedTransitions[currentStatus] || [];
    
    // Spezielle Regeln für Rollen
    if (newStatus === 'scheduled' || newStatus === 'publishing' || newStatus === 'published') {
      return this.hasRole(user, ['administrator', 'redakteur']);
    }

    if (newStatus === 'rework_requested') {
      return this.hasRole(user, ['administrator', 'redakteur']);
    }

    return allowed.includes(newStatus);
  }

  // Berechtigung für Artikel prüfen
  private static canAccessArticle(article: Article, user: User): boolean {
    // Administrator kann alles
    if (this.hasRole(user, ['administrator'])) {
      return true;
    }

    // Redakteur kann alle Artikel sehen
    if (this.hasRole(user, ['redakteur'])) {
      return true;
    }

    // Autor kann nur eigene Artikel sehen
    return article.authorId === user.id;
  }

  // Rolle prüfen
  private static hasRole(user: User, roles: UserRole[]): boolean {
    return user.roles.some(role => roles.includes(role));
  }

  // Appwrite-Dokument zu Article-Interface mappen
  private static mapDocumentToArticle(doc: any): Article {

    // Konvertiere getFileView URLs zu getFileDownload URLs für imageStorageUrl
    let processedImageStorageUrl = doc.imageStorageUrl || '';
    if (doc.imageStorageUrl && doc.imageStorageUrl.trim() !== '') {
      try {
        // Prüfe, ob es eine getFileView URL ist (enthält /view?)
        if (doc.imageStorageUrl.includes('/view?') && !doc.imageStorageUrl.includes('/download/')) {
          processedImageStorageUrl = StorageService.convertToAuthenticatedUrl(doc.imageStorageUrl);
        }
      } catch (error) {
        processedImageStorageUrl = doc.imageStorageUrl;
      }
    }

    // Sources aus String-Array parsen
    let sources = [];
    if (doc.sources && Array.isArray(doc.sources)) {
      try {
        sources = doc.sources.map((s: string) => JSON.parse(s));
      } catch (error) {
        console.warn('Fehler beim Parsen der Sources:', error);
        sources = [];
      }
    }

    return {
      id: doc.$id,
      authorId: doc.authorId,
      platformId: doc.platformId,
      status: doc.status,
      publicationState: doc.publicationState || undefined,
      publicationRequestId: doc.publicationRequestId || undefined,
      publicationPayload: doc.publicationPayload || undefined,
      publicationError: doc.publicationError || '',
      publishedUrl: doc.publishedUrl || undefined,
      publicationCandidateUrl: doc.publicationCandidateUrl || undefined,
      publicationCheckedAt: doc.publicationCheckedAt || undefined,
      type: doc.type || 'text', // Standard ist 'text', für Audio-Artikel 'audio'
      topic: doc.topic || '',
      title: doc.title,
      subtitle: doc.subtitle || '',
      content: doc.content,
      originalContent: doc.originalContent,
      categoryId: doc.categoryId,
      dossierId: doc.dossierId,
      teaser: doc.teaser || '',
      ...resolveArticleMetadata(doc),
      tags: parseTags(doc.tags),
      imagePrompt: doc.imagePrompt,
      imageType: doc.imageType || 'redaktion',
      imageSource: doc.imageSource || '',
      imageSourceUrl: doc.imageSourceUrl || '',
      imageStorageUrl: processedImageStorageUrl,
      imageCaption: doc.imageCaption || '',
      imageCaptionFromPrompt: doc.imageCaptionFromPrompt || '',
      lektoratIntensity: doc.lektoratIntensity || '',
      lektoratContent: doc.lektoratContent || '',
      originalArticleUrl: doc.originalArticleUrl || '',
      originalArticleTitle: doc.originalArticleTitle || '',
      audioStorageUrl: doc.audioStorageUrl || '',
      sources: sources, // Geparste Sources
      revisions: doc.revisions || '',
      createdAt: new Date(doc.$createdAt),
      updatedAt: new Date(doc.$updatedAt),
      scheduledAt: doc.scheduledAt || undefined, // Behalte als String
      publishedAt: doc.publishedAt ? new Date(doc.publishedAt) : undefined,
    };
  }
}
